import { CohortDTO, ExplorerCohortsService, PaginatedDTO_CohortDTO_, falconEndpoints } from '@om1/falcon-api'
import { handleApiError } from '@om1/falcon-api/utils'
import { all, call, put, select } from 'redux-saga/effects'
import { fetchDataWithCacheGenerator } from '../../../../apps/platform/src/query-utils'
import { CohortListActionTypes, CohortListItem, CohortOwner, CohortState, cohortListActions } from '../state'

export function createGetCohortsSaga() {
    return function* (
        action:
            | ReturnType<typeof cohortListActions.cohortsGetUserCohorts>
            | ReturnType<typeof cohortListActions.cohortsGetSystemCohorts>
            | ReturnType<typeof cohortListActions.cohortsGetAllCohorts>
    ) {
        yield put(cohortListActions.loadingSet({ loading: true }))
        try {
            if (action.type === CohortListActionTypes.COHORTS_ACCESS && 'cohortId' in action.payload && action.payload.cohortId) {
                const cohortDTO: CohortDTO = yield fetchDataWithCacheGenerator(
                    { cohortId: action.payload.cohortId },
                    ExplorerCohortsService.getExplorerCohortsCohortIdGet
                )

                // Only add to state if it doesn't already exist there
                let cohorts = yield select((state: { cohort: CohortState }) => state.cohort.list.cohorts[CohortOwner.User])

                let delId = action.payload.deletedId
                if (action.payload.deletedId) {
                    cohorts = cohorts.filter((cohort) => cohort.id !== delId)
                }
                const isNewCohort = !cohorts.some((cohort) => cohort.id === cohortDTO.id)
                if (isNewCohort) {
                    const meta = yield select((state: { cohort: CohortState }) => state.cohort.list.paginationMeta)
                    yield put(
                        cohortListActions.cohortsSet({
                            cohorts: [...cohorts, cohortDTO as CohortListItem],
                            meta,
                            owner: CohortOwner.User
                        })
                    )
                }
            } else if (action.payload.owner !== undefined) {
                if (action.payload.owner === 'ALL') {
                    // Extract parameters for ALL cohorts call
                    const userParams = action.payload.query?.USER || {}
                    const systemParams = action.payload.query?.SYSTEM || {}

                    // Make both API calls in parallel
                    const [systemCohortsDTO, userCohortsDTO]: [PaginatedDTO_CohortDTO_, PaginatedDTO_CohortDTO_] = yield all([
                        call(
                            fetchDataWithCacheGenerator,
                            {
                                page: systemParams.page ?? falconEndpoints.cohorts.queryParams.page,
                                limit: 100, // Get more system cohorts
                                query: systemParams.searchValue,
                                isSystem: true
                            },
                            ExplorerCohortsService.indexExplorerCohortsGet
                        ),
                        call(
                            fetchDataWithCacheGenerator,
                            {
                                page: userParams.page ?? falconEndpoints.cohorts.queryParams.page,
                                limit: userParams.pageSize ?? 50,
                                query: userParams.searchValue,
                                isSystem: false
                            },
                            ExplorerCohortsService.indexExplorerCohortsGet
                        )
                    ])

                    // Update system cohorts
                    yield put(
                        cohortListActions.cohortsSet({
                            cohorts: systemCohortsDTO.data as CohortListItem[],
                            meta: systemCohortsDTO.meta,
                            owner: CohortOwner.System
                        })
                    )

                    // Update user cohorts
                    yield put(
                        cohortListActions.cohortsSet({
                            cohorts: userCohortsDTO.data as CohortListItem[],
                            meta: userCohortsDTO.meta,
                            owner: CohortOwner.User
                        })
                    )
                } else {
                    // Original single cohort type logic
                    const apiParams = {
                        page: (action.payload as any).page ?? falconEndpoints.cohorts.queryParams.page,
                        limit: (action.payload as any).pageSize ?? falconEndpoints.cohorts.queryParams.limit,
                        query: (action.payload as any).searchValue,
                        isSystem: action.payload.owner === CohortOwner.System
                    }

                    const paginatedCohortsDTO: PaginatedDTO_CohortDTO_ = yield fetchDataWithCacheGenerator(
                        apiParams,
                        ExplorerCohortsService.indexExplorerCohortsGet
                    )
                    let cohorts = yield select((state: { cohort: CohortState }) => state.cohort.list.cohorts[action.payload.owner!])
                    let delId = (action.payload as any).deletedId
                    if ((action.payload as any).deletedId) {
                        cohorts = cohorts.filter((cohort) => cohort.id !== delId)
                    }
                    // Filter out cohorts that already exist in the state

                    const newCohorts = paginatedCohortsDTO.data.filter((cohortDTO) => {
                        return !cohorts.some((cohort) => cohort.id === cohortDTO.id)
                    })

                    yield put(
                        cohortListActions.cohortsSet({
                            cohorts: [...cohorts, ...newCohorts],
                            meta: paginatedCohortsDTO.meta,
                            owner: action.payload.owner === CohortOwner.System ? CohortOwner.System : CohortOwner.User
                        })
                    )
                }
            } else {
                const apiParams = {
                    page: (action.payload as any).query?.SYSTEM?.page ?? falconEndpoints.cohorts.queryParams.page,
                    limit: (action.payload as any).query?.SYSTEM?.pageSize ?? 100,
                    isSystem: true
                }

                const paginatedSystemCohortsDTO: PaginatedDTO_CohortDTO_ = yield fetchDataWithCacheGenerator(
                    apiParams,
                    ExplorerCohortsService.indexExplorerCohortsGet
                )

                yield put(
                    cohortListActions.cohortsSet({
                        cohorts: paginatedSystemCohortsDTO.data.map((cohortDTO) => {
                            return cohortDTO as CohortListItem
                        }),
                        meta: paginatedSystemCohortsDTO.meta,
                        owner: CohortOwner.System
                    })
                )
            }
        } catch (error: any) {
            yield handleApiError(error)
        } finally {
            yield put(cohortListActions.loadingSet({ loading: false }))
        }
    }
}

export type GetCohortsSaga = ReturnType<typeof createGetCohortsSaga>
